/**
 * Core DropdownMenu Component
 *
 * Context/action menu with icons, separators, disabled items, danger items,
 * keyboard shortcuts, nested submenus, and RBAC-gated items.
 *
 * @example
 *   <DropdownMenu
 *     trigger={<IconButton><MoreVertical /></IconButton>}
 *     items={[
 *       { label: 'Edit', icon: <Edit3 />, onClick: handleEdit },
 *       { type: 'separator' },
 *       { label: 'Delete', icon: <Trash2 />, danger: true, onClick: handleDelete, permission: 'datasources:delete' },
 *     ]}
 *   />
 */
import React, { useState, useRef } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Typography, Box, IconButton } from '@mui/material';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface DropdownMenuItem {
  type?: 'item' | 'separator' | 'header';
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Danger styling (red text) */
  danger?: boolean;
  /** Disable this item */
  disabled?: boolean;
  /** RBAC: hide if user lacks permission */
  permission?: string;
  /** Keyboard shortcut display */
  shortcut?: string;
  /** Description under label */
  description?: string;
}

export interface DropdownMenuProps {
  /** Trigger element */
  trigger: React.ReactNode;
  /** Menu items */
  items: DropdownMenuItem[];
  /** Placement */
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  /** Min width */
  minWidth?: number;
  /** Disabled */
  disabled?: boolean;
  /** Close on item click (default: true) */
  closeOnClick?: boolean;
  className?: string;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  items,
  placement = 'bottom-end',
  minWidth = 180,
  disabled = false,
  closeOnClick = true,
  className = '',
}) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (item: DropdownMenuItem) => {
    item.onClick?.();
    if (closeOnClick) handleClose();
  };

  // Map placement to MUI origins
  const anchorOrigin = placement.includes('start')
    ? { vertical: placement.includes('top') ? 'top' as const : 'bottom' as const, horizontal: 'left' as const }
    : { vertical: placement.includes('top') ? 'top' as const : 'bottom' as const, horizontal: 'right' as const };
  const transformOrigin = {
    vertical: placement.includes('top') ? 'bottom' as const : 'top' as const,
    horizontal: anchorOrigin.horizontal,
  };

  // Filter items by RBAC
  const visibleItems = items.filter(
    (item) => !item.permission || hasPermission(user, item.permission),
  );

  return (
    <>
      <Box onClick={handleOpen} sx={{ display: 'inline-flex', cursor: disabled ? 'default' : 'pointer' }}>
        {trigger}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={className}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: {
              minWidth,
              borderRadius: 'var(--border-radius, 8px)',
              mt: 0.5,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        {visibleItems.map((item, idx) => {
          if (item.type === 'separator') return <Divider key={idx} sx={{ my: 0.5 }} />;
          if (item.type === 'header') {
            return (
              <Typography
                key={idx}
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 0.75, display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <MenuItem
              key={idx}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              sx={{
                py: 1,
                px: 1.5,
                mx: 0.5,
                borderRadius: 'calc(var(--border-radius, 8px) - 2px)',
                ...(item.danger ? { color: 'error.main', '&:hover': { bgcolor: 'error.lighter' } } : {}),
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 32, color: item.danger ? 'error.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, fontSize: '0.8125rem' }}
                secondaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }}
              />
              {item.shortcut && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                  {item.shortcut}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
export default DropdownMenu;
